import { JsonLd } from "../components/JsonLd";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useLocalePath } from "../hooks/useLocalePath";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, ChevronRight, ChevronDown, ShieldCheck, Globe2,
  HelpCircle, Compass, GraduationCap, Briefcase, XCircle,
  CheckCircle2, AlertTriangle, Users, MapPin,
} from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { DVGuidesNav } from "../components/DVGuidesNav";

const REQUIREMENTS = [
  {
    title: "Country of Birth Requirement",
    icon: Globe2,
    color: "bg-teal-700 text-white",
    content: "You must be a native of a country that is eligible for the DV Lottery. Each year, the U.S. Department of State publishes a list of ineligible countries — these are countries that have sent more than 50,000 immigrants to the United States in the previous five years. As of recent years, ineligible countries typically include: Bangladesh, Brazil, Canada, China (mainland), Colombia, Dominican Republic, El Salvador, Guatemala, Haiti, Honduras, India, Jamaica, Mexico, Nigeria, Pakistan, Philippines, South Korea, United Kingdom (except Northern Ireland), and Vietnam. The list changes each year, so always check the official DV Lottery instructions for the current year.",
    note: "Important exception: If you were born in an ineligible country but your spouse was born in an eligible country, you may be able to \"charge\" your entry to your spouse's country of birth. Similarly, if neither of your parents was born in or resided in your birth country at the time of your birth, you may claim nativity in one of your parents' countries if it is eligible.",
  },
  {
    title: "Education Requirement",
    icon: GraduationCap,
    color: "bg-sky-600 text-white",
    content: "You must have at least a high school education or its equivalent. This means you must have completed a 12-year course of formal elementary and secondary education. A GED or other equivalent certificate is acceptable if it is recognized as equivalent to a U.S. high school diploma. Partial completion of high school does not qualify — you must have the full diploma or equivalent.",
    note: "Alternative: If you do not meet the education requirement, you can qualify through work experience instead (see below).",
  },
  {
    title: "Work Experience Requirement (Alternative)",
    icon: Briefcase,
    color: "bg-violet-600 text-white",
    content: "If you do not have a high school diploma, you can still qualify if you have at least two years of work experience within the past five years in an occupation that requires at least two years of training or experience. The occupation must be classified as Job Zone 4 or 5 (or certain Job Zone 3 occupations with an SVP of 7.0 or higher) according to the U.S. Department of Labor's O*NET database. This includes skilled occupations such as electricians, plumbers, nurses, chefs, mechanics, and many technical roles.",
    note: "Unskilled labor, basic retail, or general farming typically do not qualify. If unsure, check your occupation at onetonline.org.",
  },
  {
    title: "Age Requirement",
    icon: Users,
    color: "bg-amber-600 text-white",
    content: "There is no explicit minimum age requirement for the DV Lottery. However, since you must meet either the education or work experience requirement, applicants are typically at least 18 years old in practice. Minors who have completed high school can theoretically apply. There is no maximum age limit.",
    note: "",
  },
];

const DISQUALIFICATIONS = [
  "You were born in an ineligible country and cannot claim nativity through a spouse or parent",
  "You do not have a high school diploma (or equivalent) AND do not have the required work experience",
  "You have been convicted of a crime of moral turpitude or a drug offense",
  "You are subject to immigration bars (previous deportation, visa fraud, unlawful presence bars)",
  "You submitted more than one entry for the same DV Lottery year (this disqualifies ALL your entries)",
  "You are a citizen or national of an ineligible country with no cross-chargeability exception",
  "You previously received a DV visa but abandoned your permanent resident status",
];

const EDGE_CASES = [
  {
    title: "Born in an ineligible country, but spouse is from an eligible country",
    answer: "You CAN apply. You may \"cross-charge\" to your spouse's country of birth. Both you and your spouse must be listed on the application, and both must enter the U.S. together or your spouse must already have status.",
    eligible: true,
  },
  {
    title: "Born in an ineligible country, but parents were not born there",
    answer: "You MAY be eligible. If neither of your parents was born in or was a resident of your country of birth at the time of your birth, you can claim nativity in one of your parents' countries of birth — but only if that country is eligible.",
    eligible: true,
  },
  {
    title: "Born in an eligible country but currently living in an ineligible country",
    answer: "You ARE eligible. Eligibility is based on your country of BIRTH, not your country of residence or citizenship. If you were born in an eligible country, you can apply regardless of where you currently live.",
    eligible: true,
  },
  {
    title: "Dual citizen of an ineligible country",
    answer: "Your citizenship does not matter — only your country of BIRTH. If you were born in an eligible country, having citizenship of an ineligible country does not affect your eligibility.",
    eligible: true,
  },
  {
    title: "Have a criminal record",
    answer: "Having a criminal record does not automatically disqualify you from entering the lottery. However, certain crimes (crimes of moral turpitude, drug offenses, multiple criminal convictions) can make you inadmissible to the U.S., which means even if selected, you could be denied a visa at the interview stage.",
    eligible: false,
  },
  {
    title: "Currently in the U.S. on a visa",
    answer: "You CAN apply for the DV Lottery while in the U.S. on any visa status (tourist, student, H-1B, etc.). If selected, you can adjust your status in the U.S. through Form I-485 instead of consular processing — but the process must complete before September 30.",
    eligible: true,
  },
  {
    title: "Already have a pending immigration petition",
    answer: "Yes, you can apply for the DV Lottery even if you have a pending family-based or employment-based petition. However, if your DV visa is issued first, the other petition becomes irrelevant. There is no rule against having multiple immigration pathways in progress.",
    eligible: true,
  },
  {
    title: "Applied last year and was not selected",
    answer: "You CAN apply again. Each year's lottery is completely independent. Not being selected last year has no effect on your chances this year. You can apply every year that your country is eligible.",
    eligible: true,
  },
];

const FAQS = [
  {
    q: "What countries are eligible for the DV Lottery?",
    a: "Most countries are eligible. The main ineligible countries (for recent DV programs) include Bangladesh, Brazil, Canada, China (mainland), Colombia, Dominican Republic, El Salvador, Guatemala, Haiti, Honduras, India, Jamaica, Mexico, Nigeria, Pakistan, Philippines, South Korea, United Kingdom (except Northern Ireland), and Vietnam. The list is updated annually — check the official DV Lottery instructions each year.",
  },
  {
    q: "Can I apply if I only have a GED?",
    a: "Yes. A GED is accepted as equivalent to a U.S. high school diploma for DV Lottery eligibility purposes, as long as it is officially recognized by the issuing authority in your state or country.",
  },
  {
    q: "Can both my spouse and I submit separate entries?",
    a: "Yes. Both spouses can submit separate entries, which effectively doubles your chances. Each person must list the other as a derivative on their application. If either person is selected, the entire family can apply for the visa.",
  },
  {
    q: "Do I need a passport to apply for the DV Lottery?",
    a: "No, you do not need a passport to submit your DV Lottery entry. However, you will need a valid passport for the DS-260 application and visa interview if you are selected. It is wise to start the passport process early.",
  },
  {
    q: "Is there an age limit for the DV Lottery?",
    a: "There is no official minimum or maximum age. However, you must meet the education requirement (high school diploma) or work experience requirement, which practically means most applicants are 18 or older.",
  },
  {
    q: "Can I apply if I previously overstayed a U.S. visa?",
    a: "You can submit a DV Lottery entry. However, if you accrued unlawful presence in the U.S. (overstayed more than 180 days), you may be subject to a 3-year or 10-year bar from re-entering the U.S. This could prevent you from obtaining the visa even if selected. Consult an immigration attorney if this applies to you.",
  },
  {
    q: "What happens if my country becomes ineligible after I apply?",
    a: "Eligibility is determined at the time of application based on the official instructions for that year's DV program. If your country was eligible when you applied, your entry remains valid even if the country becomes ineligible in subsequent years.",
  },
];

export default function DVEligibilityPage() {
  const lp = useLocalePath();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-background" data-testid="dv-eligibility-page">
      <SEOHead
        title="DV Lottery Eligibility 2026: Requirements, Rules & Disqualifications"
        description="Who can apply for the DV Lottery in 2026? Country requirements, education and work experience rules, 7 disqualifications, and 8 edge cases with clear answers."
      />

      {/* JSON-LD Article Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "DV Lottery Eligibility 2026: Requirements, Rules & Disqualifications",
            description: "Who can apply for the DV Lottery in 2026? Country requirements, education and work experience rules, disqualifications, and edge cases.",
            dateModified: "2026-02-01",
            url: "https://immigrationguide.us/dv-lottery-eligibility",
            publisher: { "@type": "Organization", name: "ImmigrationGuide", url: "https://immigrationguide.us" },
            mainEntityOfPage: { "@type": "WebPage", "@id": "https://immigrationguide.us/dv-lottery-eligibility" },
          }} />

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }} />

      {/* HERO */}
      <section className="hero-gradient py-14 md:py-24" data-testid="dve-hero">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <Badge variant="secondary" className="bg-cyan-100 text-cyan-800 hover:bg-cyan-100 mb-5 font-medium">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            Eligibility Guide
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-5">
            DV Lottery Eligibility 2026: Who Qualifies and What Disqualifies You
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-slate-600 mb-8 max-w-2xl mx-auto">
            Not everyone can enter the Diversity Visa Lottery. You must meet requirements related to your country
            of birth, education, and work experience. Below are the four core requirements, seven disqualifications,
            and eight real-world edge cases based on current U.S. Department of State rules.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/how-to-fill-dv-lottery-form")} data-testid="dve-hero-form">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-800 text-white font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto px-8 py-6 text-base">
                <MapPin className="mr-2 h-4 w-4" /> How to Apply
              </Button>
            </Link>
            <Link to={lp("/gc")} data-testid="dve-hero-gc">
              <Button size="lg" variant="outline" className="border-slate-200 hover:bg-slate-50 font-medium w-full sm:w-auto px-8 py-6 text-base">
                <Compass className="mr-2 h-4 w-4" /> Full Green Card Guide
              </Button>
            </Link>
          </div>
          <div className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-teal-600" />
            <span>Last updated: February 2026 · Based on official U.S. Department of State rules</span>
          </div>
        </div>
      </section>

      {/* CORE REQUIREMENTS */}
      <section className="py-14 md:py-20 bg-white" data-testid="dve-requirements">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">Core Requirements</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              What You Need to Qualify
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-5">
            {REQUIREMENTS.map((req, i) => {
              const Icon = req.icon;
              return (
                <Card key={i} className="border-slate-100 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${req.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-slate-900 mb-2">{req.title}</h3>
                        <p className="text-sm leading-relaxed text-slate-600 mb-3">{req.content}</p>
                        {req.note && (
                          <div className="bg-slate-50 rounded-lg p-3 text-sm">
                            <p className="text-slate-600"><span className="font-semibold text-slate-700">Note: </span>{req.note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="dve-mid-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <p className="text-sm text-slate-600">
            Think you qualify?{" "}
            <a href="https://immigrationguide.us/auth?mode=register" target="_blank" rel="noopener noreferrer" className="text-teal-700 font-medium hover:text-teal-800 underline underline-offset-2">
              Understand what each step means
            </a>{" "}
            with our free AI-powered immigration guide.
          </p>
        </div>
      </section>

      {/* DISQUALIFICATIONS */}
      <section className="py-14 md:py-20 bg-slate-50" data-testid="dve-disqualifications">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-rose-600 mb-2">Disqualifications</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              What Can Disqualify You
            </h2>
            <p className="text-sm text-slate-600 mt-3 max-w-xl mx-auto">
              Even if you meet the basic requirements, certain factors can disqualify your entry or make you inadmissible.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {DISQUALIFICATIONS.map((d, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg border border-rose-100 bg-rose-50/50">
                <XCircle className="h-4 w-4 text-rose-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDGE CASES */}
      <section className="py-14 md:py-20 bg-white" data-testid="dve-edge-cases">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-violet-600 mb-2">Special Situations</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              Edge Cases & Examples
            </h2>
            <p className="text-sm text-slate-600 mt-3 max-w-xl mx-auto">
              Real-world scenarios that people commonly ask about.
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {EDGE_CASES.map((ec, i) => (
              <Card key={i} className={`shadow-sm ${ec.eligible ? "border-emerald-100" : "border-amber-100"}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    {ec.eligible ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{ec.title}</h3>
                      <p className="text-sm leading-relaxed text-slate-600">{ec.answer}</p>
                      <Badge variant="secondary" className={`mt-2 text-xs ${ec.eligible ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {ec.eligible ? "Likely eligible" : "May face issues"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* INTERNAL LINKS */}
      <section className="py-10 bg-slate-50 border-t border-slate-100" data-testid="dve-internal-links">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Related Green Card Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to={lp("/gc")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all bg-white">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Green Card Hub</h3>
              <p className="text-xs text-slate-500">Complete guide to all Green Card pathways</p>
            </Link>
            <Link to={lp("/gc/green-card-guide")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all bg-white">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Green Card Categories</h3>
              <p className="text-xs text-slate-500">Family, employment, lottery, and more</p>
            </Link>
            <Link to={lp("/gc/green-card-timeline")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all bg-white">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Processing Timeline</h3>
              <p className="text-xs text-slate-500">How long each category takes step by step</p>
            </Link>
            <Link to={lp("/gc/documents-checklist")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all bg-white">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Documents Checklist</h3>
              <p className="text-xs text-slate-500">32 required documents organized by category</p>
            </Link>
            <Link to={lp("/gc/faq")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all bg-white">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Green Card FAQ</h3>
              <p className="text-xs text-slate-500">19 common questions answered clearly</p>
            </Link>
            <Link to={lp("/how-to-check-dv-lottery-results")} className="group p-4 rounded-lg border border-slate-200 hover:border-teal-200 hover:shadow-sm transition-all bg-white">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-teal-700 transition-colors mb-1">Check DV Lottery Results</h3>
              <p className="text-xs text-slate-500">Official steps to check if you were selected</p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-white" data-testid="dve-faq">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-wider font-semibold text-teal-600 mb-2">Common Questions</p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
              DV Lottery Eligibility FAQ
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQS.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <Card key={i} className="border-slate-200 shadow-sm overflow-hidden">
                  <button
                    className="w-full flex items-start gap-3 p-5 text-left cursor-pointer"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    data-testid={`dve-faq-toggle-${i}`}
                  >
                    <HelpCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-semibold text-slate-900 flex-1">{faq.q}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600 ml-8">{faq.a}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-24 bg-teal-700" data-testid="dve-final-cta">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-5" />
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-3">
            Start Your Green Card Journey
          </h2>
          <p className="text-base text-teal-100 mb-8 max-w-xl mx-auto">
            Eligible for the DV Lottery? Our AI-powered platform guides you through the entire application process step by step.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lp("/gc")} data-testid="dve-final-gc">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium shadow-lg w-full sm:w-auto px-6 py-6 text-base">
                Green Card Guide <ArrowRight className="mr-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to={lp("/auth?mode=register")} data-testid="dve-final-start">
              <Button size="lg" variant="outline" className="border-teal-400 text-white hover:bg-teal-600 font-medium w-full sm:w-auto px-6 py-6 text-base">
                Start Free Guide <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <DVGuidesNav currentPath="/dv-lottery-eligibility" />
      <SiteFooter />
    </div>
  );
}
